import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Lock,
  LogOut,
  Sparkles,
  Download,
  Smartphone,
  BellRing,
  Newspaper,
  Crown,
  Calendar,
  Trophy,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSapphires } from '../context/SapphireContext';
import { useToast } from '../hooks/use-toast';
import { CLASSES } from '../mock';
import GoogleSignInButton from '../components/GoogleSignInButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APK_URL =
  'https://customer-assets.emergentagent.com/job_monline-revival/artifacts/c0jhgh4n_NewRealm21.05.apk';

const AuthForms = () => {
  const { login, register } = useAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    charClass: 'Fire',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === 'login') {
        await login({ email: form.email, password: form.password });
        toast({ title: 'Welcome back, adventurer!' });
      } else {
        if (!form.username || form.username.length < 2) {
          throw new Error('Username must be at least 2 characters.');
        }
        if (!form.password || form.password.length < 4) {
          throw new Error('Password must be at least 4 characters.');
        }
        await register(form);
        toast({ title: 'Account created!', description: `Welcome to Eldoria, ${form.username}.` });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Something went wrong.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset-request`, {
        email: forgotEmail.trim(),
        site_url: window.location.origin,
      });
    } catch {
      /* swallow, generic response */
    } finally {
      setForgotSent(true);
      setForgotLoading(false);
    }
  };

  const closeForgot = () => {
    setForgotOpen(false);
    setForgotSent(false);
    setForgotEmail('');
  };

  return (
    <>
      <div className="max-w-md mx-auto rounded-2xl border border-white/10 bg-black/60 p-8 backdrop-blur fade-up">
        <div className="flex gap-2 p-1 rounded-lg bg-black/60 border border-white/10 mb-6">
          {['login', 'register'].map((m) => (
            <button
              key={m}
              onClick={() => {
                setMode(m);
                setError(null);
              }}
              className={`flex-1 font-nav text-xs py-2 rounded-md transition-colors ${
                mode === m ? 'bg-[#f5b400] text-black' : 'text-white/70 hover:text-white'
              }`}
            >
              {m === 'login' ? 'SIGN IN' : 'CREATE ACCOUNT'}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === 'register' && (
            <div>
              <label className="font-nav text-[10px] tracking-widest text-white/60">USERNAME</label>
              <div className="mt-1 flex items-center gap-2 bg-black/50 border border-white/15 focus-within:border-[#f5b400] rounded-md px-3 py-2 transition-colors">
                <User size={16} className="text-[#f5b400]" />
                <input
                  type="text"
                  value={form.username}
                  onChange={onChange('username')}
                  placeholder="Adventurer Name"
                  className="flex-1 bg-transparent outline-none text-white text-sm placeholder:text-white/30"
                  required
                  minLength={2}
                />
              </div>
            </div>
          )}

          <div>
            <label className="font-nav text-[10px] tracking-widest text-white/60">EMAIL</label>
            <div className="mt-1 flex items-center gap-2 bg-black/50 border border-white/15 focus-within:border-[#f5b400] rounded-md px-3 py-2 transition-colors">
              <Mail size={16} className="text-[#f5b400]" />
              <input
                type="email"
                value={form.email}
                onChange={onChange('email')}
                placeholder="you@eldoria.com"
                className="flex-1 bg-transparent outline-none text-white text-sm placeholder:text-white/30"
                required
              />
            </div>
          </div>

          <div>
            <label className="font-nav text-[10px] tracking-widest text-white/60">PASSWORD</label>
            <div className="mt-1 flex items-center gap-2 bg-black/50 border border-white/15 focus-within:border-[#f5b400] rounded-md px-3 py-2 transition-colors">
              <Lock size={16} className="text-[#f5b400]" />
              <input
                type="password"
                value={form.password}
                onChange={onChange('password')}
                placeholder="••••••••"
                className="flex-1 bg-transparent outline-none text-white text-sm placeholder:text-white/30"
                required
                minLength={4}
              />
            </div>
            {mode === 'login' && (
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => {
                    setForgotEmail(form.email);
                    setForgotOpen(true);
                  }}
                  className="font-nav text-[10px] tracking-widest text-white/60 hover:text-[#f5b400] transition-colors"
                >
                  FORGOT PASSWORD?
                </button>
              </div>
            )}
          </div>

          {mode === 'register' && (
            <div>
              <label className="font-nav text-[10px] tracking-widest text-white/60">CHOOSE CLASS</label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {CLASSES.map((c) => (
                  <button
                    type="button"
                    key={c.name}
                    onClick={() => setForm((f) => ({ ...f, charClass: c.name }))}
                    className={`text-xs font-nav py-2 rounded-md border transition-all ${
                      form.charClass === c.name
                        ? 'border-[#f5b400] bg-[#f5b400]/10 text-[#f5b400]'
                        : 'border-white/15 text-white/70 hover:border-white/40'
                    }`}
                  >
                    {c.name.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && (
            <p className="text-red-400 text-xs font-body bg-red-500/10 border border-red-500/30 rounded-md p-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#f5b400] hover:bg-[#ffc630] disabled:opacity-60 text-black font-nav text-xs py-3 rounded-md transition-colors"
          >
            {loading
              ? 'PROCESSING...'
              : mode === 'login'
                ? 'ENTER THE REALM'
                : 'BEGIN ADVENTURE'}
          </button>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="font-nav text-[10px] tracking-widest text-white/40">OR</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <GoogleSignInButton />

          <p className="text-center text-white/50 text-xs font-body">
            {mode === 'login' ? 'New to Eldoria?' : 'Already have an account?'}{' '}
            <button
              type="button"
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-[#f5b400] hover:underline"
            >
              {mode === 'login' ? 'Create one' : 'Sign in'}
            </button>
          </p>
        </form>
      </div>

      {forgotOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 fade-up"
          onClick={closeForgot}
        >
          <div
            className="relative w-full max-w-md rounded-2xl border border-[#f5b400]/40 bg-gradient-to-br from-[#1a1410] via-black to-black p-7"
            onClick={(e) => e.stopPropagation()}
          >
            {!forgotSent ? (
              <>
                <p className="font-nav text-[#f5b400] text-[10px] tracking-widest text-center">
                  PASSWORD RESET
                </p>
                <h3 className="font-heading text-white text-2xl text-center mt-1">
                  Forgot your password?
                </h3>
                <p className="font-body text-white/70 text-sm text-center mt-2">
                  Enter your account email and we'll send you a link to choose a new one.
                </p>
                <form onSubmit={submitForgot} className="mt-5 space-y-4">
                  <div className="flex items-center gap-2 bg-black/50 border border-white/15 focus-within:border-[#f5b400] rounded-md px-3 py-2 transition-colors">
                    <Mail size={16} className="text-[#f5b400]" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="you@eldoria.com"
                      required
                      className="flex-1 bg-transparent outline-none text-white text-sm placeholder:text-white/30"
                      autoFocus
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full bg-[#f5b400] hover:bg-[#ffc630] disabled:opacity-60 text-black font-nav text-xs py-3 rounded-md transition-colors"
                  >
                    {forgotLoading ? 'SENDING…' : 'SEND RESET LINK'}
                  </button>
                  <button
                    type="button"
                    onClick={closeForgot}
                    className="w-full font-nav text-[10px] text-white/55 hover:text-white tracking-widest"
                  >
                    CANCEL
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center">
                <Mail size={40} className="text-[#f5b400] mx-auto" />
                <h3 className="font-heading text-white text-2xl mt-3">Check your inbox</h3>
                <p className="font-body text-white/75 text-sm mt-2">
                  If an account exists for <b>{forgotEmail}</b>, you'll receive an email with a reset
                  link shortly. The link is valid for 1 hour.
                </p>
                <button
                  type="button"
                  onClick={closeForgot}
                  className="mt-6 bg-[#f5b400] hover:bg-[#ffc630] text-black font-nav text-xs px-6 py-3 rounded-md transition-colors"
                >
                  CLOSE
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const Toggle = ({ value, onChange }) => (
  <button
    onClick={() => onChange(!value)}
    className={`relative w-12 h-6 rounded-full transition-colors ${
      value ? 'bg-[#f5b400]' : 'bg-white/15'
    }`}
    aria-pressed={value}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-black transition-transform ${
        value ? 'translate-x-6' : ''
      }`}
    />
  </button>
);

const AccountDashboard = () => {
  const { user, logout, update, refresh } = useAuth();
  const { sapphires, setSapphires } = useSapphires();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(user.username);
  const [photoBroken, setPhotoBroken] = useState(false);

  const charClassData = CLASSES.find((c) => c.name === user.char_class) || CLASSES[0];

  // Sync user stats & balance from backend on load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/user-stats/${encodeURIComponent(user.email)}`);
        if (cancelled) return;
        if (typeof data.sapphires_balance === 'number' && setSapphires) {
          setSapphires(data.sapphires_balance);
        }
        refresh && refresh();
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.email]);

  const totalPurchased = user.total_purchased || 0;
  const accountLevel = user.level || 1 + Math.floor(totalPurchased / 1000);
  const nextThreshold = accountLevel * 1000;
  const progress = Math.min(100, Math.round(((totalPurchased % 1000) / 1000) * 100));

  const saveName = async () => {
    if (nameDraft.trim().length >= 2) {
      try {
        await update({ username: nameDraft.trim() });
        toast({ title: 'Name updated' });
      } catch (e) {
        toast({ title: 'Could not update', description: e.message, variant: 'destructive' });
      }
    }
    setEditingName(false);
  };

  const onLogout = () => {
    logout();
    toast({ title: 'Signed out', description: 'Until next time, hero.' });
    navigate('/');
  };

  const memberSince = new Date(user.member_since).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="max-w-5xl mx-auto fade-up">
      <div className="rounded-2xl border border-[#f5b400]/30 bg-gradient-to-br from-black/70 via-[#1a1410]/60 to-black/70 p-8 flex flex-col md:flex-row items-center gap-6 backdrop-blur shadow-[0_0_40px_-15px_rgba(245,180,0,0.55)]">
        <div
          className="relative w-28 h-28 rounded-full flex items-center justify-center border-4 shrink-0"
          style={{ borderColor: charClassData.color, background: `${charClassData.color}22` }}
        >
          {user.picture && !photoBroken ? (
            <img
              src={user.picture}
              alt={user.username}
              referrerPolicy="no-referrer"
              onError={() => setPhotoBroken(true)}
              data-testid="account-profile-photo"
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <Crown size={42} style={{ color: charClassData.color }} />
          )}
          <span
            className="absolute -bottom-2 left-1/2 -translate-x-1/2 font-nav text-[10px] tracking-widest text-black px-2 py-0.5 rounded-full"
            style={{ background: charClassData.color }}
          >
            LV {accountLevel}
          </span>
        </div>

        <div className="flex-1 text-center md:text-left">
          {editingName ? (
            <div className="flex gap-2 justify-center md:justify-start">
              <input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                className="bg-black/50 border border-[#f5b400]/40 rounded-md px-3 py-2 text-white text-2xl font-heading outline-none"
                autoFocus
              />
              <button
                onClick={saveName}
                className="bg-[#f5b400] text-black font-nav text-xs px-4 rounded-md"
              >
                SAVE
              </button>
            </div>
          ) : (
            <h1
              className="font-heading text-white text-3xl md:text-4xl cursor-pointer hover:text-[#f5b400] transition-colors"
              onClick={() => {
                setNameDraft(user.username);
                setEditingName(true);
              }}
              title="Click to edit"
            >
              {user.username}
            </h1>
          )}
          <p className="text-white/70 font-body text-sm mt-1">{user.email}</p>
          <div className="mt-3 flex flex-wrap gap-2 justify-center md:justify-start">
            <span
              className="font-nav text-[10px] tracking-widest px-3 py-1 rounded-full border"
              style={{
                color: charClassData.color,
                borderColor: `${charClassData.color}66`,
                background: `${charClassData.color}15`,
              }}
            >
              {(user.char_class || 'Fire').toUpperCase()} CLASS
            </span>
            <span className="font-nav text-[10px] tracking-widest px-3 py-1 rounded-full border border-white/15 text-white/65 inline-flex items-center gap-1">
              <Calendar size={11} /> JOINED {memberSince.toUpperCase()}
            </span>
          </div>
        </div>

        <button
          onClick={onLogout}
          className="font-nav text-xs text-white/70 hover:text-red-400 border border-white/15 hover:border-red-400/50 rounded-md px-4 py-2 transition-colors inline-flex items-center gap-2"
        >
          <LogOut size={14} /> SIGN OUT
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="rounded-xl border border-white/10 bg-black/45 p-6">
          <div className="flex items-center justify-between">
            <p className="font-nav text-[10px] tracking-widest text-white/55">SAPPHIRES</p>
            <Sparkles size={18} className="text-[#f5b400]" />
          </div>
          <p className="font-heading text-3xl text-[#f5b400] mt-2">{sapphires.toLocaleString()}</p>
          <button
            onClick={() => navigate('/store')}
            className="mt-3 font-nav text-[10px] tracking-widest text-white/70 hover:text-[#f5b400] transition-colors"
          >
            → BUY MORE
          </button>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/45 p-6">
          <div className="flex items-center justify-between">
            <p className="font-nav text-[10px] tracking-widest text-white/55">ACCOUNT LEVEL</p>
            <Trophy size={18} className="text-[#f5b400]" />
          </div>
          <p className="font-heading text-3xl text-white mt-2">{accountLevel}</p>
          <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#f5b400] to-[#ffc630] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="font-body text-[11px] text-white/45 mt-2">
            {totalPurchased.toLocaleString()} / {nextThreshold.toLocaleString()} Sapphires bought
          </p>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/45 p-6">
          <div className="flex items-center justify-between">
            <p className="font-nav text-[10px] tracking-widest text-white/55">ACCOUNT STATUS</p>
            <Crown size={18} className="text-[#f5b400]" />
          </div>
          <p className="font-heading text-3xl text-white mt-2">Free</p>
          <p className="font-body text-[11px] text-white/45 mt-1">Premium coming soon</p>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-[#f5b400]/40 bg-gradient-to-r from-[#f5b400]/15 via-black/60 to-black/60 p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
        <div className="w-20 h-20 rounded-2xl bg-[#f5b400]/20 border border-[#f5b400]/40 flex items-center justify-center shrink-0">
          <Smartphone size={36} className="text-[#f5b400]" />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="font-heading text-white text-2xl">PLAY THE DEMO ON ANDROID</h3>
          <p className="font-body text-white/75 text-sm mt-1">
            Try an early demo of New Realm Online on your Android device — a sneak peek of Eldoria
            while we keep building the full game.
          </p>
        </div>
        <a
          href={APK_URL}
          target="_blank"
          rel="noreferrer"
          download
          className="bg-[#f5b400] hover:bg-[#ffc630] text-black font-nav text-xs px-6 py-3 rounded-md inline-flex items-center gap-2 transition-colors"
        >
          <Download size={16} /> DOWNLOAD APK
        </a>
      </div>
      <p className="mt-2 text-center md:text-right text-white/55 font-body text-[11px] italic">
        Available only for Android for now — iOS &amp; PC coming soon.
      </p>

      <div className="mt-6 rounded-2xl border border-white/10 bg-black/45 p-6 md:p-8">
        <h3 className="font-heading text-white text-xl mb-5">SETTINGS</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <BellRing size={18} className="text-[#f5b400]" />
              <div>
                <p className="font-heading text-white text-sm">Push notifications</p>
                <p className="font-body text-xs text-white/55">Get alerts for in-game events</p>
              </div>
            </div>
            <Toggle
              value={!!user.settings?.notifications}
              onChange={(v) => update({ settings: { notifications: v } })}
            />
          </div>
          <div className="h-px bg-white/8" />
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Newspaper size={18} className="text-[#f5b400]" />
              <div>
                <p className="font-heading text-white text-sm">Newsletter</p>
                <p className="font-body text-xs text-white/55">Monthly updates from the dev team</p>
              </div>
            </div>
            <Toggle
              value={!!user.settings?.newsletter}
              onChange={(v) => update({ settings: { newsletter: v } })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const AccountPage = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="pt-28 pb-20 bg-[#0a0608] min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#f5b400] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="pt-28 pb-20 bg-[#0a0608] min-h-screen">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        {!user && (
          <>
            <div className="mb-12 text-center fade-up">
              <p className="font-nav text-[#f5b400] text-xs tracking-widest">ACCOUNT</p>
              <h1 className="font-heading section-heading-white mt-3">WELCOME TO</h1>
              <h1 className="font-heading section-heading-gold">YOUR ADVENTURE</h1>
              <p className="mt-4 max-w-2xl mx-auto text-white/75 font-body text-sm">
                Sign in or create an account to track your Sapphires, save your class, and download the
                game.
              </p>
            </div>
            <AuthForms />
          </>
        )}
        {user && <AccountDashboard />}
      </div>
    </div>
  );
};

export default AccountPage;
